import {
  UilAward,
  UilBookmark,
  UilBookOpen,
  UilDashboard,
  UilFileCheck,
  UilPricetagAlt,
  UilTruck,
} from "@lde/vue-unicons";

import IconFileDevis from "@/components/icons/IconFileDevis.vue";
import IconPapier from "@/components/icons/IconPapier.vue";
import IconPapierAlt from "@/components/icons/IconPapierAlt.vue";
import IconNumerique from "@/components/icons/IconNumerique.vue";
import IconFourniture from "@/components/icons/IconFourniture.vue";
import IconEstablishment from "@/components/icons/IconEstablishment.vue";
import i18n from "@/modules/i18n";
import { getUrls } from "@/router";
import store from "@/store";

function getTitle(key, count) {
  return () => i18n.global.t(key, count);
}

const COMMANDES_FACTURES_ROUTE = "commandes_factures";
const COMMANDES_ROUTE = "commandes";
const LDC_ROUTE = "ldc";

/** Nom de la route des commandes (généré par router/routes.js). */
export const COMMANDES_ROUTE_NAME = [COMMANDES_FACTURES_ROUTE, COMMANDES_ROUTE].join("_");
/** Nom de la route des lignes de commande (généré par router/routes.js). */
export const LDC_ROUTE_NAME = [COMMANDES_ROUTE_NAME, LDC_ROUTE].join("_");
/** Paramètre d'URL filtrant les lignes de commande sur le restant à livrer. */
export const RESTANT_A_LIVRER_QUERY = "restant_a_livrer";
/** Lien vers les lignes de commande filtrées sur le restant à livrer. */
export const RESTANT_A_LIVRER_ROUTE = {
  name: LDC_ROUTE_NAME,
  query: { [RESTANT_A_LIVRER_QUERY]: 1 },
};

export const CATALOGUE_ROUTE_BY_TYPE = {
  papier: "livres_manuels_papier",
  numerique: "ressources_numeriques",
  fourniture: "fournitures_papeterie",
};

const CATALOGUE_PERMISSION_BY_TYPE = {
  papier: ["view_articlepapier"],
  numerique: ["view_manuelnumerique"],
  fourniture: ["view_fourniture"],
};

function getCatalogueTypeFromSlug(catalogueSlug) {
  if (!catalogueSlug) {
    return null;
  }

  const activeCatalogues = store.getters.activeCataloguesForMenu;
  return activeCatalogues.find((cat) => cat?.slug === catalogueSlug)?.type || null;
}

function ensureCatalogueRouteType(expectedType, to, next) {
  const catalogueSlug = to.params?.catalogueSlug;
  const actualType = getCatalogueTypeFromSlug(catalogueSlug);

  if (!catalogueSlug || !actualType) {
    next();
    return;
  }

  const expectedRouteName = `catalogues_${CATALOGUE_ROUTE_BY_TYPE[expectedType]}`;
  const actualRouteName = `catalogues_${CATALOGUE_ROUTE_BY_TYPE[actualType]}`;

  if (expectedRouteName !== actualRouteName) {
    next({
      name: actualRouteName,
      params: { ...to.params, catalogueSlug },
      query: to.query,
      hash: to.hash,
    });
    return;
  }

  next();
}

/**
 * Retourne le premier catalogue actif et accessible selon l'ordre métier des types.
 * @param {Array} activeCatalogues
 * @returns {Object|null}
 */
function getFirstAccessibleCatalogue(activeCatalogues) {
  const orderedTypes = ["papier", "numerique", "fourniture"];

  return orderedTypes.reduce((found, type) => {
    if (found) {
      return found;
    }

    if (!store.getters.hasPerms(CATALOGUE_PERMISSION_BY_TYPE[type])) {
      return null;
    }

    return activeCatalogues.find((cat) => cat?.type === type && cat?.slug) || null;
  }, null);
}

/**
 * Génère 1 sous-menu par catalogue actif en conservant la route cible selon le type.
 * @param {Array} submenuTemplates Sous-menus statiques "modèles" du parent catalogues.
 * @param {Array} activeCatalogues Catalogues actifs provenant de /identify.
 * @returns {Array}
 */
export function buildCataloguesSubmenu(submenuTemplates = [], activeCatalogues = []) {
  if (!Array.isArray(submenuTemplates) || !submenuTemplates.length) {
    return [];
  }

  const templatesByName = submenuTemplates.reduce((acc, sm) => ({ ...acc, [sm.name]: sm }), {});

  return activeCatalogues
    .map((catalogue, index) => {
      const templateName = CATALOGUE_ROUTE_BY_TYPE[catalogue?.type];
      const template = templateName ? templatesByName[templateName] : null;

      if (!template || !catalogue?.slug) {
        return null;
      }

      const label = catalogue?.nom || template.title();
      const uniqueName = `${template.name}_${catalogue.slug}`;

      return {
        ...template,
        key: `catalogue_${catalogue.type}_${catalogue.slug}_${index}`,
        name: uniqueName,
        title: () => label,
        routerLinkObj: {
          name: `catalogues_${templateName}`,
          params: { catalogueSlug: catalogue.slug },
        },
      };
    })
    .filter(Boolean);
}

/* Voir /store/index.js:hasPerms pour les explications de permissions */
export default [
  {
    ...getUrls("url.catalogues"),
    name: "catalogues",
    title: getTitle("menu.catalogues-lde"),
    icon: UilBookOpen,
    cataloguePublic: true,
    beforeEnter: (to, from, next) => {
      const activeCatalogues = store.getters.activeCataloguesForMenu;
      const hasTypedCatalogs = activeCatalogues.length > 0;

      if (!hasTypedCatalogs) {
        if (store.getters.hasPerms(["view_articlepapier"])) {
          next({ name: "catalogues_livres_manuels_papier" });
          return;
        }
        if (store.getters.hasPerms(["view_manuelnumerique"])) {
          next({ name: "catalogues_ressources_numeriques" });
          return;
        }
        if (store.getters.hasPerms(["view_fourniture"])) {
          next({ name: "catalogues_fournitures_papeterie" });
          return;
        }
        next({ name: "403" });
        return;
      }

      const firstCatalogue = getFirstAccessibleCatalogue(activeCatalogues);
      if (!firstCatalogue) {
        next({ name: "403" });
        return;
      }

      next({
        name: `catalogues_${CATALOGUE_ROUTE_BY_TYPE[firstCatalogue.type]}`,
        params: { catalogueSlug: firstCatalogue.slug },
      });
    },
    submenu: [
      {
        ...getUrls("url.livres_manuels_papier"),
        name: "livres_manuels_papier",
        title: getTitle("menu.livres-et-manuels-papier"),
        permissions: ["view_articlepapier"],
        beforeEnter: (to, from, next) => ensureCatalogueRouteType("papier", to, next),
        icon: IconPapier,
        component: () => import("@/views/LivresManuelsPapier.vue"),
        submenu: [
          {
            ...getUrls(":id"),
            name: "item",
            goBackBtn: true,
            permissions: ["view_articlepapier"],
            component: () => import("@/views/FicheLivrePapier.ts.vue"),
          },
        ],
      },
      {
        ...getUrls("url.ressources_numeriques"),
        name: "ressources_numeriques",
        title: getTitle("menu.ressources-numeriques"),
        icon: IconNumerique,
        cataloguePublic: true,
        permissions: ["view_manuelnumerique"],
        beforeEnter: (to, from, next) => ensureCatalogueRouteType("numerique", to, next),
        component: () => import("@/views/RessourcesNumeriques.vue"),
        submenu: [
          {
            ...getUrls(":id"),
            name: "item",
            cataloguePublic: true,
            permissions: ["view_manuelnumerique"],
            goBackBtn: true,
            component: () => import("@/views/FicheManuelNumerique.vue"),
          },
        ],
      },
      {
        ...getUrls("url.fournitures_papeterie"),
        name: "fournitures_papeterie",
        title: getTitle("menu.fournitures-et-papeterie"),
        permissions: ["view_fourniture"],
        beforeEnter: (to, from, next) => ensureCatalogueRouteType("fourniture", to, next),
        component: () => import("@/views/FournituresPapeterie.vue"),
        icon: IconFourniture,
        submenu: [
          {
            ...getUrls(":id"),
            name: "item",
            permissions: ["view_fourniture"],
            goBackBtn: true,
            component: () => import("@/views/FicheFourniture.vue"),
          },
        ],
      },
    ],
  },
  {
    ...getUrls("url.listes_devis"),
    name: "listes_devis",
    title: getTitle("menu.listes"),
    icon: UilBookmark,
    redirect: { name: "listes_devis_listes" },
    permissions: [["view_liste"], ["view_devis"]],
    submenu: [
      {
        path: "",
        name: "saisie_rapide",
        title: getTitle("menu.saisie-rapide"),
        icon: UilBookmark,
        redirect: { name: "listes_devis_listes", query: { saisieRapide: true } },
      },
      {
        ...getUrls("url.listes"),
        name: "listes",
        title: getTitle("menu.vos-listes"),
        icon: UilBookmark,
        permissions: ["view_liste"],
        component: () => import("@/views/Liste.vue"),
        submenu: [
          {
            ...getUrls(":id"),
            name: "item",
            icon: UilBookmark,
            permissions: ["view_liste"],
            goBackBtn: true,
            component: () => import("@/views/FicheListe.vue"),
          },
        ],
      },
      {
        ...getUrls("url.devis"),
        name: "devis",
        title: getTitle("menu.vos-devis"),
        icon: IconFileDevis,
        permissions: ["view_devis", "!can_view_as_maitre_compta"],
        component: () => import("@/views/Devis.ts.vue"),
        submenu: [
          {
            path: "/ldd",
            name: "ldd",
            title: getTitle("devis.ligne-de-devis", 2),
            icon: UilTruck,
            permissions: ["view_devis"],
            goBackBtn: true,
            component: () => import("@/views/LDDView.ts.vue"),
          },
          {
            ...getUrls(":id"),
            name: "item",
            icon: IconFileDevis,
            permissions: ["view_devis"],
            goBackBtn: true,
            component: () => import("@/views/FicheDevis.vue"),
          },
        ],
      },
      {
        ...getUrls("url.devis_soumis"),
        name: "devis_soumis",
        title: getTitle("menu.vos-devis-soumis"),
        icon: IconFileDevis,
        permissions: ["view_devis", "can_view_as_maitre_compta"],
        component: () => import("@/views/Devis.ts.vue"),
      },
      {
        ...getUrls("url.devis_non_soumis"),
        name: "devis_non_soumis",
        title: getTitle("menu.vos-devis-non-soumis"),
        icon: IconFileDevis,
        permissions: ["view_devis", "can_view_as_maitre_compta"],
        component: () => import("@/views/Devis.ts.vue"),
      },
    ],
  },
  {
    ...getUrls("url.commandes_factures"),
    name: COMMANDES_FACTURES_ROUTE,
    title: getTitle("menu.commandes"), // Ou "Commandes & factures" => Défini dans App.vue selon perm
    icon: UilFileCheck,
    redirect: { name: COMMANDES_ROUTE_NAME },
    // permissions: [["view_commande"], ["view_facture"], ["view_expedition"]],
    permissions: [["view_commande"], ["view_facture"]],
    submenu: [
      {
        ...getUrls("url.commandes"),
        name: COMMANDES_ROUTE,
        title: getTitle("menu.commandes"),
        icon: UilFileCheck,
        permissions: ["view_commande"],
        component: () => import("@/views/Commande.vue"),
        submenu: [
          {
            // ...getUrls("url.restant_a_livrer"),
            path: "/ldc",
            name: LDC_ROUTE,
            title: getTitle("commande.ligne-de-commande", 2),
            icon: UilTruck,
            permissions: ["view_commande"],
            goBackBtn: true,
            component: () => import("@/views/LDCView.ts.vue"),
          },
          {
            ...getUrls(":id"),
            name: "item",
            icon: UilFileCheck,
            permissions: ["view_commande"],
            goBackBtn: true,
            component: () => import("@/views/FicheCommande.vue"),
          },
        ],
      },
      {
        name: "ral",
        title: getTitle("menu.restant-a-livrer"),
        icon: UilFileCheck,
        permissions: ["view_commande"],
        routerLinkObj: RESTANT_A_LIVRER_ROUTE,
      },
      {
        ...getUrls("url.factures"),
        name: "factures",
        title: getTitle("menu.factures-avoirs"),
        icon: UilFileCheck,
        permissions: ["view_facture"],
        component: () => import("@/views/Facture.vue"),
      },
      {
        ...getUrls("url.expeditions"),
        name: "expeditions",
        title: getTitle("menu.expeditions-bl"),
        icon: UilTruck,
        permissions: ["view_expedition"],
        component: () => import("@/views/Expedition.vue"),
      },
    ],
  },
  {
    ...getUrls("url.licences"),
    name: "licences",
    title: getTitle("menu.licences-numeriques"),
    icon: UilAward,
    redirect: { name: "licences_actives" },
    permissions: ["can_commander_licence_supp"],
    submenu: [
      {
        ...getUrls("url.actives"),
        name: "actives",
        title: getTitle("menu.licences-commandees"),
        icon: IconNumerique,
        permissions: ["can_commander_licence_supp"],
        component: () => import("@/views/Licences.vue"),
        submenu: [
          {
            ...getUrls(":id"),
            name: "item",
            icon: IconNumerique,
            permissions: ["can_commander_licence_supp"],
            goBackBtn: true,
            component: () => import("@/views/FicheLicence.vue"),
          },
        ],
      },
      {
        name: "suivi_demandes_licences",
        title: getTitle("suivi-licences.suivi-des-demandes-de-licences"),
        icon: UilFileCheck,
        routerLinkObj: { name: "suivi_demandes_licences" },
      },
    ],
  },
  {
    ...getUrls("url.stocks_physiques"),
    name: "stocks_physiques",
    title: getTitle("menu.stocks-et-etiquettes"),
    icon: IconPapierAlt,
    redirect: { name: "stocks_physiques_etiquettes" },
    permissions: ["use_cristalweb", "view_articlepapier", "can_order_etiquette"],
    submenu: [
      {
        ...getUrls("url.etiquettes"),
        name: "etiquettes",
        title: getTitle("menu.commande-etiquettes"),
        icon: UilPricetagAlt,
        permissions: ["use_cristalweb", "view_articlepapier", "can_order_etiquette"],
        component: () => import("@/views/Etiquettes.vue"),
      },
      {
        title: getTitle("general.douchettes"),
        name: "douchette",
        permissions: ["use_cristalweb"],
        beforeEnter: (to, from, next) => {
          next({
            name: "search",
            query: store.getters.isGMBH ? {
              search: "lesegerät",
              only: "papiers__livres-manuels-papier",
              "papiers__livres-manuels-papier_editeur": 26833,
            } : {
              search: "douchette",
              only: "papiers__livres-manuels-papier",
              "papiers__livres-manuels-papier_editeur": 5028,
            },
          });
        },
      },
      {
        title: getTitle("general.cristal-web"),
        name: "cristal_web",
        extLink: store.getters.urlCristalWeb, // Redéfini dans App.vue selon perm pour le num serveur
        permissions: ["use_cristalweb"],
      },
    ],
  },
  {
    ...getUrls("url.etablissements"),
    name: "etablissements",
    title: getTitle("menu.mes-etablissements", 1),
    icon: IconEstablishment,
    redirect: { name: "etablissements_actuel" },
    cataloguePublic: false,
    submenu: [
      {
        ...getUrls("url.tous"),
        name: "tous",
        title: getTitle("menu.tableau-bord-suivi"),
        icon: UilDashboard,
        permissions: ["can_view_as_maitre_compta"],
        component: () => import("@/views/EtablissementRegion.vue"),
        submenu: [
          {
            ...getUrls(":id"),
            name: "item",
            icon: IconEstablishment,
            permissions: ["can_view_as_maitre_compta"],
            goBackBtn: true,
            component: () => import("@/views/FicheEtablissement.vue"),
          },
        ],
      },
      {
        path: "etablissement",
        name: "etablissement",
        icon: IconEstablishment,
        permissions: ["can_view_as_directeur_etablissement"],
        component: () => import("@/views/FicheEtablissement.vue"),
      },
      {
        ...getUrls("url.actuel"),
        name: "actuel",
        title: getTitle("menu.mon-compte"),
        icon: IconEstablishment,
        component: () => import("@/views/FicheEtablissement.vue"),
      },
    ],
  },
];
